import React, { useState } from 'react';
import invoiceLookupData from '../data/invoiceLookup.json';
import apiClient from '../services/apiClient';

const CoPilot = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [couponData, setCouponData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [allChecked, setAllChecked] = useState(false);
  
  // Individual checkbox states
  const [checkboxes, setCheckboxes] = useState({
    dateRedeemed: false,
    discount: false,
    qualifyingCriteria: false,
    totalSpend: false,
    discountApplied: false
  });

  // Helper function to check if two values match (with tolerance for floating point)
  const valuesMatch = (val1, val2, tolerance = 0.01) => {
    const num1 = parseFloat(val1) || 0;
    const num2 = parseFloat(val2) || 0;
    return Math.abs(num1 - num2) <= tolerance;
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setError(null);
    }
  };

  // Search local JSON file for invoice data
  const searchLocalInvoiceData = (filename) => {
    console.log('[CoPilot] Searching local invoice lookup data for:', filename);
    
    // Extract filename from URL if needed
    let searchFilename = filename;
    if (filename.includes('/') || filename.includes('http')) {
      const urlParts = filename.split('/');
      searchFilename = urlParts[urlParts.length - 1];
    }
    
    const filenameWithoutExt = searchFilename.replace(/\.[^/.]+$/, '');
    
    // Try multiple search patterns
    const searchPatterns = [
      searchFilename,
      filenameWithoutExt,
      searchFilename.toLowerCase(),
      filenameWithoutExt.toLowerCase(),
      `https://v2-media-api.hillscoupons.co.za/media-asset/${searchFilename}`,
      `https://v2-media-api.hillscoupons.co.za/media-asset/${filenameWithoutExt}.pdf`
    ];
    
    // Search through the invoice lookup data
    for (const invoice of invoiceLookupData) {
      // Check multiple possible field names for the URI
      const invoiceUri = invoice.coupon_redemption_proof_of_purchase_uri || 
                        invoice.invoiceUri || 
                        invoice.invoice_uri || 
                        invoice.proof_of_purchase_uri || 
                        '';
      const invoiceFilename = invoice.filename || '';
      
      // Check if any search pattern matches
      for (const pattern of searchPatterns) {
        if (
          invoiceUri.includes(pattern) ||
          invoiceUri.toLowerCase().includes(pattern.toLowerCase()) ||
          invoiceFilename === pattern ||
          invoiceFilename.toLowerCase() === pattern.toLowerCase()
        ) {
          console.log('[CoPilot] ✓ Found match in local data:', invoice);
          return invoice;
        }
      }
    }
    
    console.log('[CoPilot] ✗ No match found in local data');
    return null;
  };

  // Extract filename and search local JSON file
  const handleSubmit = async () => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const filename = selectedFile.name;
      console.log('[CoPilot] Searching for filename:', filename);
      
      // Search local JSON file
      const match = searchLocalInvoiceData(filename);
      
      if (match) {
        // Map the local data to the format expected by the component
        // Handle both Excel field names and expected component field names
        setCouponData({
          ...match,
          coupon_code: match.coupon_code || match.couponCode,
          coupon_actual_code: match.coupon_actual_code,
          invoice_number: match.invoice_number || match.invoiceNumber,
          invoice_uri: match.coupon_redemption_proof_of_purchase_uri || 
                     match.invoiceUri || 
                     match.invoice_uri,
          retailer: match.retailer_name || match.retailer || match.store,
          retailer_name: match.retailer_name,
          retail_staff_first_name: match.retail_staff_first_name,
          retail_staff_last_name: match.retail_staff_last_name,
          redeemed_at: match.coupon_redeemed_at || match.redeemedAt || match.redeemed_at,
          discount_applied: match.coupon_redemption_discount_applied || match.discountApplied,
          coupon_redemption_discount_applied: match.coupon_redemption_discount_applied,
          coupon_redemption_last_approved_at: match.coupon_redemption_last_approved_at,
          coupon_basket_cost: match.coupon_basket_cost,
          campaign_maximum_discount: match.campaign_maximum_discount,
          calculated_discount: match.ai_calculatedDiscount || match.calculatedDiscount,
          ai_calculatedDiscount: match.ai_calculatedDiscount,
          detected_discount: match.ai_detectedDiscount || match.detectedDiscount,
          ai_detectedDiscount: match.ai_detectedDiscount,
          amount: match.coupon_basket_cost || match.amount,
          basket_size: match.coupon_basket_size,
          campaign_name: match.campaign_name
        });
        setInvoiceNumber(match.invoice_number || match.invoiceNumber || '');
        setAllChecked(true);
        // Initialize checkboxes - validate discount applied against AI detected discount
        const discountAppliedMatches = valuesMatch(
          match.coupon_redemption_discount_applied, 
          match.ai_detectedDiscount
        );
        // Validate total spend: coupon_basket_cost = (ai_calculatedDiscount x 4), except when ai_calculatedDiscount = 500
        const calculatedDiscount = match.ai_calculatedDiscount || 0;
        const basketCost = match.coupon_basket_cost || 0;
        const totalSpendMatches = calculatedDiscount === 500 || valuesMatch(basketCost, calculatedDiscount * 4);
        
        setCheckboxes({
          dateRedeemed: true,
          discount: true,
          qualifyingCriteria: true,
          totalSpend: totalSpendMatches,
          discountApplied: discountAppliedMatches
        });
        console.log('[CoPilot] ✓ Successfully loaded invoice data');
      } else {
        const filenameWithoutExt = filename.replace(/\.[^/.]+$/, '');
        setError(
          `No matching invoice found for "${filename}". ` +
          `\n\nDocument ID: ${filenameWithoutExt}` +
          `\n\nThis invoice is not in the local lookup file. ` +
          `Please add it to src/data/invoiceLookup.json or try entering the invoice number manually.`
        );
      }
    } catch (err) {
      console.error('[CoPilot] Error searching for invoice:', err);
      setError('Failed to search for invoice. Please try entering the invoice number manually.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle manual invoice number entry
  const handleInvoiceSearch = async () => {
    if (!invoiceNumber.trim()) {
      setError('Please enter an invoice number');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Search local JSON file first
      const match = invoiceLookupData.find(invoice => 
        (invoice.invoiceNumber || invoice.invoice_number || '').toLowerCase() === invoiceNumber.trim().toLowerCase() ||
        (invoice.coupon_code || '').toLowerCase() === invoiceNumber.trim().toLowerCase() ||
        (invoice.coupon_actual_code || '').toLowerCase() === invoiceNumber.trim().toLowerCase()
      );
      
      if (match) {
        console.log('[CoPilot] ✓ Found invoice in local data:', match);
        setCouponData({
          ...match,
          coupon_code: match.coupon_code || match.couponCode,
          coupon_actual_code: match.coupon_actual_code,
          invoice_number: match.invoice_number || match.invoiceNumber,
          invoice_uri: match.coupon_redemption_proof_of_purchase_uri || 
                     match.invoiceUri || 
                     match.invoice_uri,
          retailer: match.retailer_name || match.retailer || match.store,
          retailer_name: match.retailer_name,
          retail_staff_first_name: match.retail_staff_first_name,
          retail_staff_last_name: match.retail_staff_last_name,
          redeemed_at: match.coupon_redeemed_at || match.redeemedAt || match.redeemed_at,
          discount_applied: match.coupon_redemption_discount_applied || match.discountApplied,
          coupon_redemption_discount_applied: match.coupon_redemption_discount_applied,
          coupon_redemption_last_approved_at: match.coupon_redemption_last_approved_at,
          coupon_basket_cost: match.coupon_basket_cost,
          campaign_maximum_discount: match.campaign_maximum_discount,
          calculated_discount: match.ai_calculatedDiscount || match.calculatedDiscount,
          ai_calculatedDiscount: match.ai_calculatedDiscount,
          detected_discount: match.ai_detectedDiscount || match.detectedDiscount,
          ai_detectedDiscount: match.ai_detectedDiscount,
          amount: match.coupon_basket_cost || match.amount,
          basket_size: match.coupon_basket_size,
          campaign_name: match.campaign_name
        });
        setAllChecked(true);
        // Initialize checkboxes - validate discount applied against AI detected discount
        const discountAppliedMatches = valuesMatch(
          match.coupon_redemption_discount_applied, 
          match.ai_detectedDiscount
        );
        // Validate total spend: coupon_basket_cost = (ai_calculatedDiscount x 4), except when ai_calculatedDiscount = 500
        const calculatedDiscount = match.ai_calculatedDiscount || 0;
        const basketCost = match.coupon_basket_cost || 0;
        const totalSpendMatches = calculatedDiscount === 500 || valuesMatch(basketCost, calculatedDiscount * 4);
        
        setCheckboxes({
          dateRedeemed: true,
          discount: true,
          qualifyingCriteria: true,
          totalSpend: totalSpendMatches,
          discountApplied: discountAppliedMatches
        });
      } else {
        // Fallback to API if not found locally
        console.log('[CoPilot] Not found locally, trying API...');
        try {
          const response = await apiClient.get(`/redemption/search-by-invoice?invoice=${encodeURIComponent(invoiceNumber)}`);
          if (response.data) {
            setCouponData(response.data);
            setAllChecked(true);
          } else {
            setError('No coupon found with this invoice number in local data or API');
          }
        } catch (apiErr) {
          setError('No coupon found with this invoice number. Please check the invoice number and try again.');
        }
      }
    } catch (err) {
      console.error('[CoPilot] Error searching for invoice:', err);
      setError('Failed to find coupon with this invoice number');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle approve for payment
  const handleApprove = async () => {
    if (!couponData) return;

    try {
      await apiClient.post('/redemption/approve', {
        couponCode: couponData.coupon_code,
        approvedAt: new Date().toISOString()
      });
      
      alert('Payment approved successfully!');
      // Reset form
      handleReset();
    } catch (err) {
      console.error('Error approving payment:', err);
      alert('Failed to approve payment. Please try again.');
    }
  };

  // Handle dispute
  const handleDispute = async () => {
    if (!couponData) return;

    const reason = prompt('Please enter dispute reason:');
    if (!reason) return;

    try {
      await apiClient.post('/redemption/dispute', {
        couponCode: couponData.coupon_code,
        disputeReason: reason,
        disputedAt: new Date().toISOString()
      });
      
      alert('Coupon disputed successfully!');
      handleReset();
    } catch (err) {
      console.error('Error disputing coupon:', err);
      alert('Failed to dispute coupon. Please try again.');
    }
  };

  // Handle checkbox changes
  const handleCheckboxChange = (checkboxName) => {
    setCheckboxes(prev => ({
      ...prev,
      [checkboxName]: !prev[checkboxName]
    }));
  };

  // Reset form
  const handleReset = () => {
    setSelectedFile(null);
    setCouponData(null);
    setInvoiceNumber('');
    setAllChecked(false);
    setError(null);
    setCheckboxes({
      dateRedeemed: false,
      discount: false,
      qualifyingCriteria: false,
      totalSpend: false,
      discountApplied: false
    });
  };

  // Get invoice type
  const getInvoiceType = (uri) => {
    if (!uri) return 'unknown';
    const extension = uri.split('.').pop()?.toLowerCase();
    if (['pdf'].includes(extension)) return 'pdf';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)) return 'image';
    return 'unknown';
  };

  // Check if calculated discount matches expected values
  const isCalculatedDiscountValid = (couponData) => {
    if (!couponData) return false;
    const calculated = couponData.ai_calculatedDiscount;
    const basketCost = couponData.coupon_basket_cost;
    const discountApplied = couponData.coupon_redemption_discount_applied;
    
    // Check if calculated discount matches basket cost or discount applied
    return valuesMatch(calculated, basketCost) || valuesMatch(calculated, discountApplied);
  };

  // Check if detected discount matches expected values
  const isDetectedDiscountValid = (couponData) => {
    if (!couponData) return false;
    const detected = couponData.ai_detectedDiscount;
    const basketCost = couponData.coupon_basket_cost;
    const discountApplied = couponData.coupon_redemption_discount_applied;
    
    // Check if detected discount matches basket cost or discount applied
    return valuesMatch(detected, basketCost) || valuesMatch(detected, discountApplied);
  };

  // Validation functions for each checkbox
  const isDateRedeemedValid = (couponData) => {
    // Date redeemed is always valid if it exists
    return !!couponData?.redeemed_at;
  };

  const isQualifyingCriteriaValid = (couponData) => {
    // Qualifying criteria is always valid (always "All products")
    return true;
  };

  const isDiscountValid = (couponData) => {
    // Check if calculated discount matches expected values
    return isCalculatedDiscountValid(couponData);
  };

  const isTotalSpendValid = (couponData) => {
    if (!couponData) return false;
    // Check if total_spend matches coupon_basket_cost
    return valuesMatch(couponData.total_spend, couponData.coupon_basket_cost);
  };

  const isDiscountAppliedValid = (couponData) => {
    // Check if detected discount matches discount applied
    return isDetectedDiscountValid(couponData);
  };

  return (
    <div style={{ padding: '20px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ 
        background: 'linear-gradient(135deg, #0d2f5c 0%, #1a4d7d 50%, #0d2f5c 100%)',
        padding: '20px',
        marginBottom: '20px',
        borderRadius: '4px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h1 style={{ 
          color: '#fff', 
          margin: 0, 
          fontSize: '28px',
          textAlign: 'center',
          letterSpacing: '1px'
        }}>
          🤖 CO-PILOT - Coupon Validation Assistant
        </h1>
      </div>

      {/* Main Content */}
      <div style={{ display: 'flex', gap: '20px' }}>
        {/* Left Panel - Input & Validation */}
        <div style={{ flex: '1', maxWidth: '720px' }}>
          
          {/* File Upload Section */}
          <div style={{ 
            backgroundColor: '#fff', 
            padding: '20px', 
            borderRadius: '4px', 
            marginBottom: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ 
              color: '#0d2f5c', 
              marginTop: 0,
              fontSize: '18px',
              borderBottom: '2px solid #0d2f5c',
              paddingBottom: '10px'
            }}>
              Upload Invoice/Till Slip
            </h3>
            
            <div style={{ marginBottom: '15px' }}>
              <input 
                type="file" 
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileSelect}
                style={{ 
                  padding: '10px',
                  width: '100%',
                  border: '2px dashed #0d2f5c',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              />
              {selectedFile && (
                <p style={{ marginTop: '10px', color: '#28a745', fontSize: '14px' }}>
                  ✓ Selected: {selectedFile.name}
                </p>
              )}
            </div>

            <button 
              onClick={handleSubmit}
              disabled={!selectedFile || isLoading}
              style={{
                backgroundColor: '#28a745',
                color: '#fff',
                padding: '12px 24px',
                border: 'none',
                borderRadius: '4px',
                cursor: selectedFile && !isLoading ? 'pointer' : 'not-allowed',
                fontSize: '16px',
                fontWeight: 'bold',
                opacity: selectedFile && !isLoading ? 1 : 0.5,
                width: '100%'
              }}
            >
              {isLoading ? 'SEARCHING...' : 'SUBMIT'}
            </button>

            {error && (
              <div style={{ 
                marginTop: '15px', 
                padding: '10px', 
                backgroundColor: '#f8d7da', 
                color: '#721c24',
                borderRadius: '4px',
                fontSize: '14px'
              }}>
                {error}
              </div>
            )}
          </div>

          {/* Coupon Details Display */}
          {couponData && (
            <>
              {/* Header Bar */}
              <div style={{ 
                backgroundColor: '#fff',
                padding: '15px 20px',
                borderRadius: '4px 4px 0 0',
                borderBottom: '3px solid #0d2f5c',
                marginTop: '20px'
              }}>
                <h2 style={{ 
                  margin: 0, 
                  fontSize: '16px', 
                  color: '#333',
                  textTransform: 'uppercase'
                }}>
                  COUPON DETAILS (COUPON CODE: {couponData.coupon_code}) 
                  (COUPON REDEMPTION DATE: {new Date(couponData.redeemed_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase()})
                </h2>
              </div>

              {/* Invoice Number Section */}
              <div style={{ backgroundColor: '#fff', padding: '20px', marginBottom: '20px' }}>
                <div style={{ 
                  backgroundColor: '#0d2f5c', 
                  color: '#fff', 
                  padding: '10px 15px',
                  marginBottom: '15px',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}>
                  INVOICE NUMBER
                </div>
                
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <input 
                    type="text"
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                    placeholder="Enter Invoice Number:"
                    style={{
                      flex: 1,
                      padding: '10px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                  <button 
                    onClick={handleInvoiceSearch}
                    style={{
                      backgroundColor: '#28a745',
                      color: '#fff',
                      padding: '10px 20px',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    SUBMIT
                  </button>
                  <button 
                    onClick={handleDispute}
                    style={{
                      backgroundColor: '#dc3545',
                      color: '#fff',
                      padding: '10px 20px',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    DISPUTE ▼
                  </button>
                </div>
              </div>

              {/* Outlet Info Section */}
              <div style={{ backgroundColor: '#fff', padding: '20px', marginBottom: '20px' }}>
                <div style={{ 
                  backgroundColor: '#0d2f5c', 
                  color: '#fff', 
                  padding: '10px 15px',
                  marginBottom: '15px',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}>
                  OUTLET INFO
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', fontSize: '14px' }}>
                  <div>
                    <span style={{ fontWeight: 'bold' }}>Outlet Name:</span><br/>
                    <span style={{ fontWeight: 'normal' }}>{couponData.retailer_name || 'N/A'}</span>
                  </div>
                  <div>
                    <span style={{ fontWeight: 'bold' }}>Staff Member Redeeming Coupon:</span><br/>
                    <span style={{ fontWeight: 'normal' }}>
                      {couponData.retail_staff_first_name || couponData.retail_staff_last_name 
                        ? `${(couponData.retail_staff_first_name || '').trim()} ${(couponData.retail_staff_last_name || '').trim()}`.trim() || 'N/A'
                        : 'N/A'}
                    </span>
                  </div>
                </div>

                <div style={{ 
                  marginTop: '15px',
                  padding: '12px',
                  backgroundColor: '#e9ecef',
                  borderRadius: '4px'
                }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input 
                      type="checkbox" 
                      checked={allChecked}
                      readOnly
                      style={{ marginRight: '10px', width: '18px', height: '18px' }}
                    />
                    <span style={{ fontWeight: 'bold' }}>Outlet detail matches Invoice</span>
                  </label>
                </div>
              </div>

              {/* Campaign Details Section */}
              <div style={{ backgroundColor: '#fff', padding: '20px', marginBottom: '20px' }}>
                <div style={{ 
                  backgroundColor: '#0d2f5c', 
                  color: '#fff', 
                  padding: '10px 15px',
                  marginBottom: '15px',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}>
                  CAMPAIGN DETAILS
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', fontSize: '14px' }}>
                  <div>
                    <span style={{ fontWeight: 'bold' }}>Coupon Campaign Name:</span><br/>
                    <span style={{ fontWeight: 'normal' }}>{couponData.campaign_name || 'N/A'}</span>
                  </div>
                  <div>
                    <span style={{ fontWeight: 'bold' }}>Coupon Code:</span><br/>
                    <span style={{ fontWeight: 'normal' }}>{couponData.coupon_code || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Validation Checkboxes Section */}
              <div style={{ backgroundColor: '#fff', padding: '20px', marginBottom: '20px' }}>
                <div style={{ 
                  backgroundColor: '#0d2f5c', 
                  color: '#fff', 
                  padding: '10px 15px',
                  marginBottom: '15px',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}>
                  VALIDATION CHECKS
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '15px' }}>
                  {/* Date of Issue */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontWeight: 'bold' }}>Date of Issue:</span><br/>
                      <span style={{ fontWeight: 'normal' }}>
                        {couponData.coupon_redemption_last_approved_at 
                          ? new Date(couponData.coupon_redemption_last_approved_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) 
                          : 'N/A'}
                      </span>
                    </div>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontWeight: 'bold' }}>Discount:</span><br/>
                      <span style={{ fontWeight: 'normal' }}>
                        {couponData.coupon_redemption_discount_applied !== null && couponData.coupon_redemption_discount_applied !== undefined
                          ? `R${couponData.coupon_redemption_discount_applied.toFixed(2)}`
                          : 'N/A'}
                      </span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontWeight: 'bold' }}>Qualifying Criteria:</span><br/>
                      <span style={{ fontWeight: 'normal' }}>All products</span>
                    </div>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontWeight: 'bold' }}>Total Spend:</span><br/>
                      <span style={{ fontWeight: 'normal' }}>
                        {couponData.coupon_basket_cost !== null && couponData.coupon_basket_cost !== undefined
                          ? `R${couponData.coupon_basket_cost.toFixed(2)}`
                          : 'R0.00'}
                      </span>
                    </div>
                  </div>

                  <div>
                    <span style={{ fontWeight: 'bold' }}>Maximum Discount Received:</span><br/>
                    <span style={{ fontWeight: 'normal' }}>
                      {couponData.campaign_maximum_discount !== null && couponData.campaign_maximum_discount !== undefined
                        ? `R${couponData.campaign_maximum_discount.toFixed(2)}`
                        : 'R0.00'}
                    </span>
                  </div>

                  {/* Validation Checkboxes */}
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 1fr',
                    gap: '20px',
                    marginTop: '15px',
                    padding: '15px',
                    backgroundColor: '#e9ecef',
                    borderRadius: '4px'
                  }}>
                    <label style={{ display: 'flex', alignItems: 'flex-start', padding: '12px', minWidth: 0 }}>
                      <input 
                        type="checkbox" 
                        checked={checkboxes.dateRedeemed}
                        onChange={() => handleCheckboxChange('dateRedeemed')}
                        style={{ marginRight: '12px', marginTop: '3px', width: '18px', height: '18px', flexShrink: 0, cursor: 'pointer' }}
                      />
                      <span style={{ minWidth: 0, wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                        <span style={{ fontWeight: 'bold' }}>Date Redeemed</span><br/>
                        <small style={{ fontWeight: 'normal' }}>{new Date(couponData.redeemed_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })} matches Invoice</small>
                      </span>
                    </label>

                    <label style={{ display: 'flex', alignItems: 'flex-start', padding: '12px', minWidth: 0 }}>
                      <input 
                        type="checkbox" 
                        checked={checkboxes.discount}
                        onChange={() => handleCheckboxChange('discount')}
                        style={{ marginRight: '12px', marginTop: '3px', width: '18px', height: '18px', flexShrink: 0, cursor: 'pointer' }}
                      />
                      <span style={{ minWidth: 0, wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                        <span style={{ fontWeight: 'bold' }}>Discount</span><br/>
                        <small style={{ fontWeight: 'normal' }}>
                          {couponData.discount_percentage || 'Discount'} matches Invoice or Maximum discount allowed
                        </small>
                      </span>
                    </label>

                    <label style={{ display: 'flex', alignItems: 'flex-start', padding: '12px', minWidth: 0 }}>
                      <input 
                        type="checkbox" 
                        checked={checkboxes.qualifyingCriteria}
                        onChange={() => handleCheckboxChange('qualifyingCriteria')}
                        style={{ marginRight: '12px', marginTop: '3px', width: '18px', height: '18px', flexShrink: 0, cursor: 'pointer' }}
                      />
                      <span style={{ minWidth: 0, wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                        <span style={{ fontWeight: 'bold' }}>Qualifying Criteria</span><br/>
                        <small style={{ fontWeight: 'normal' }}>{couponData.qualifying_criteria || 'All products'} matches Invoice</small>
                      </span>
                    </label>

                    <label style={{ display: 'flex', alignItems: 'flex-start', padding: '12px', minWidth: 0 }}>
                      <input 
                        type="checkbox" 
                        checked={checkboxes.totalSpend}
                        onChange={() => handleCheckboxChange('totalSpend')}
                        style={{ marginRight: '12px', marginTop: '3px', width: '18px', height: '18px', flexShrink: 0, cursor: 'pointer' }}
                      />
                      <span style={{ minWidth: 0, wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                        <span style={{ fontWeight: 'bold' }}>Total Hill's Basket Spend</span><br/>
                        <small style={{ fontWeight: 'normal' }}>R{couponData.coupon_basket_cost?.toFixed(2) || '0.00'} matches Invoice</small>
                      </span>
                    </label>

                    <label style={{ display: 'flex', alignItems: 'flex-start', padding: '12px', gridColumn: '1 / -1', minWidth: 0 }}>
                      <input 
                        type="checkbox" 
                        checked={checkboxes.discountApplied}
                        onChange={() => handleCheckboxChange('discountApplied')}
                        style={{ marginRight: '12px', marginTop: '3px', width: '18px', height: '18px', flexShrink: 0, cursor: 'pointer' }}
                      />
                      <span style={{ minWidth: 0, wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                        <span style={{ fontWeight: 'bold' }}>Discount Applied</span><br/>
                        <small style={{ fontWeight: 'normal' }}>
                          R{couponData.discount_applied?.toFixed(2) || '0.00'} matches Invoice
                        </small>
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '15px', marginTop: '20px' }}>
                <button 
                  onClick={handleApprove}
                  disabled={!allChecked}
                  style={{
                    backgroundColor: allChecked ? '#28a745' : '#ccc',
                    color: '#fff',
                    padding: '15px 40px',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: allChecked ? 'pointer' : 'not-allowed',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    textTransform: 'uppercase'
                  }}
                >
                  Approve for Payment
                </button>
                <button 
                  onClick={handleDispute}
                  style={{
                    backgroundColor: '#dc3545',
                    color: '#fff',
                    padding: '15px 40px',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    textTransform: 'uppercase'
                  }}
                >
                  Dispute Coupon
                </button>
              </div>
            </>
          )}
        </div>

        {/* Right Panel - Preview */}
        <div style={{ flex: '1', maxWidth: '50%' }}>
          {couponData?.invoice_uri && (
            <div style={{ 
              backgroundColor: '#fff', 
              padding: '20px', 
              borderRadius: '4px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              position: 'sticky',
              top: '20px'
            }}>
              <div style={{ 
                backgroundColor: '#0d2f5c', 
                color: '#fff', 
                padding: '10px 15px',
                marginBottom: '15px',
                fontWeight: 'bold',
                fontSize: '14px',
                textAlign: 'center'
              }}>
                PROOF OF SALES INVOICE
              </div>

              <div style={{ 
                border: '2px solid #0d2f5c',
                borderRadius: '4px',
                overflow: 'hidden',
                minHeight: '600px',
                maxHeight: '800px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f8f9fa'
              }}>
                {getInvoiceType(couponData.invoice_uri) === 'pdf' ? (
                  <iframe
                    src={couponData.invoice_uri}
                    title="Invoice Preview"
                    style={{
                      width: '100%',
                      height: '600px',
                      border: 'none'
                    }}
                  />
                ) : getInvoiceType(couponData.invoice_uri) === 'image' ? (
                  <img 
                    src={couponData.invoice_uri}
                    alt="Invoice Preview"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '600px',
                      objectFit: 'contain'
                    }}
                  />
                ) : (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                    <p>Preview not available</p>
                    <a 
                      href={couponData.invoice_uri}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#0d2f5c', textDecoration: 'underline' }}
                    >
                      Open in new tab
                    </a>
                  </div>
                )}
              </div>

              <div style={{ marginTop: '15px', textAlign: 'center' }}>
                <a 
                  href={couponData.invoice_uri}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#0d2f5c',
                    textDecoration: 'none',
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}
                >
                  → Open Full Screen
                </a>
              </div>
            </div>
          )}

          {!couponData && (
            <div style={{ 
              backgroundColor: '#fff', 
              padding: '40px', 
              borderRadius: '4px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              textAlign: 'center',
              color: '#999'
            }}>
              <p style={{ fontSize: '18px', margin: 0 }}>📄</p>
              <p style={{ margin: '10px 0 0 0' }}>Upload a file to preview invoice</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoPilot;

